import apiClient, { get, post } from "@/lib/api-client";

export interface KnowledgeArticle {
  id: string;
  title: string;
  content: string;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  createdById: string;
  createdAt: string;
  updatedAt: string;
}

// 语义搜索类型
export interface SemanticSearchItem {
  id: string;
  type: "document" | "article" | "folder";
  title: string;
  webUrl?: string;
  mimeType?: string | null;
  fileExtension?: string | null;
  snippet: string;
  previewContent?: string;
  score: number;
  docType?: string;
  docAuthorityLevel?: string;
  lastModifiedAt?: string;
  createdBy?: string;
  folderPath?: string;
}

export interface SemanticSearchResponse {
  items: SemanticSearchItem[];
  total: number;
  searchType: string;
  responseTimeMs: number;
}

export interface SemanticSearchParams {
  q: string;
  limit?: number;
  type?: "keyword" | "semantic" | "hybrid";
  docType?: string;
  authorityLevel?: string;
}

export interface SearchSuggestionItem {
  text: string;
  source: "RECENT_SEARCH" | "TOP_QUERY" | "TITLE";
}

// AI 问答类型
export interface SourceDocument {
  id: string;
  type: "document" | "article";
  title: string;
  webUrl?: string;
  snippet: string;
  authorityLevel: string;
  docType: string;
  relevanceScore: number;
}

export interface AskResponse {
  id: string;
  answer: string;
  sources: SourceDocument[];
  confidence: number;
  metadata: {
    modelUsed: string;
    tokensUsed: number;
    cost: number;
    responseTimeMs: number;
  };
}

export interface AskHistoryItem {
  id: string;
  question: string;
  answer: string;
  createdAt: string;
}

// 同步任务类型
export interface SyncTaskStatus {
  taskId: string;
  totalItems: number;
  processedItems: number;
  processingItems: number;
  failedItems: number;
  skippedItems: number;
  processedTokens: number;
  processedChunks: number;
  status: "PENDING" | "SYNCING" | "COMPLETED" | "FAILED";
  error?: string;
}

export interface SyncTaskListItem {
  id: string;
  taskType: string;
  triggerSource?: string | null;
  status: "PENDING" | "SYNCING" | "COMPLETED" | "FAILED";
  totalItems: number | null;
  processedItems: number;
  processingItems: number;
  failedItems: number;
  skippedItems: number;
  processedTokens: number;
  processedChunks: number;
  error?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
}

export interface SyncTaskSkippedItem {
  id: string;
  taskId: string;
  sourceType: "SP_DOCUMENT" | "ARTICLE";
  sourceId: string;
  filename: string;
  fileExtension?: string | null;
  mimeType?: string | null;
  reason: string;
  createdAt: string;
}

export interface SyncTaskProcessedItem {
  id: string;
  taskId: string;
  sourceType: "SP_DOCUMENT" | "ARTICLE";
  sourceId: string;
  filename: string;
  fileExtension?: string | null;
  mimeType?: string | null;
  ragflowDocumentId?: string | null;
  tokenCount?: number | null;
  chunkCount?: number | null;
  sourceSizeBytes?: number | null;
  status?: "PROCESSING" | "COMPLETED";
  createdAt: string;
}

export interface SyncTaskFailedItem {
  id: string;
  taskId: string;
  sourceType: "SP_DOCUMENT" | "ARTICLE";
  sourceId: string;
  filename: string;
  fileExtension?: string | null;
  mimeType?: string | null;
  error: string;
  createdAt: string;
}

// 度量统计类型
export interface MetricsOverview {
  searchStats: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    byType: { keyword: number; semantic: number; hybrid: number };
    avgResponseTime: number;
  };
  qaStats: {
    today: number;
    thisWeek: number;
    thisMonth: number;
    avgConfidence: number;
    feedbackRate: { positive: number; negative: number; none: number };
    avgResponseTime: number;
    totalCost: number;
  };
  documentStats: {
    totalDocuments: number;
    totalArticles: number;
    indexedDocuments: number;
    pendingSync: number;
    failedSync: number;
  };
  userStats: {
    activeUsersToday: number;
    activeUsersWeek: number;
    activeUsersMonth: number;
  };
}

export interface DailyTrendItem {
  date: string;
  searches: number;
  qaQueries: number;
}

export interface TopQueryItem {
  query: string;
  count: number;
}

// 最近访问类型
export interface RecentlyVisitedItem {
  id: string;
  type: "document" | "article";
  title: string;
  webUrl?: string;
  lastViewedAt: string;
  editedAt?: string;
  docType?: string;
}

// 活动动态类型
export interface ActivityItem {
  id: string;
  type: "comment" | "mention" | "like";
  actorId: string;
  actorName: string;
  targetType: "document" | "article";
  targetId: string;
  targetTitle: string;
  content?: string;
  createdAt: string;
  isRead?: boolean;
}

// 用户 Analytics 类型
export interface UserAnalytics {
  createdArticles: {
    draft: number;
    published: number;
  };
  views: number;
  reads: number;
  likes: number;
  dislikes: number;
}

export const knowledgeBaseApi = {
  // 搜索建议（输入联想）
  searchSuggestions: (params: { q: string; limit?: number }) =>
    get<{ items: SearchSuggestionItem[] }>(
      "/knowledge-base/search-suggestions",
      { params },
    ),
  deleteSearchHistory: (params?: { query?: string }) =>
    apiClient.delete<{ deleted: number }, { deleted: number }>(
      "/knowledge-base/search-history",
      { params },
    ),

  // 语义搜索
  semanticSearch: (params: SemanticSearchParams) =>
    get<SemanticSearchResponse>("/knowledge-base/semantic-search", { params }),

  // 文件上传
  upload: (file: File) => {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post("/knowledge-base/upload", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },

  // 文章 CRUD
  // 注意：apiClient 响应拦截器已自动解包 response.data.data，所以这里返回的直接是 KnowledgeArticle
  createArticle: (data: { title: string; content: string }) =>
    apiClient.post<KnowledgeArticle, KnowledgeArticle>(
      "/knowledge-base/articles",
      data,
    ),
  updateArticle: (id: string, data: { title: string; content: string }) =>
    apiClient.put<KnowledgeArticle, KnowledgeArticle>(
      `/knowledge-base/articles/${id}`,
      data,
    ),
  getArticle: (id: string) =>
    get<KnowledgeArticle>(`/knowledge-base/articles/${id}`),

  // AI 问答
  // 注意：apiClient 响应拦截器已自动解包 response.data.data，返回的是实际数据
  ask: (
    question: string,
    options?: { docType?: string; authorityLevel?: string },
  ): Promise<AskResponse> =>
    apiClient.post("/knowledge-base/ask", {
      question,
      docType: options?.docType,
      authorityLevel: options?.authorityLevel,
    }) as Promise<AskResponse>,

  submitFeedback: (
    id: string,
    feedback: "THUMBS_UP" | "THUMBS_DOWN",
    comment?: string,
  ) =>
    apiClient.post(`/knowledge-base/ask/${id}/feedback`, { feedback, comment }),

  getAskHistory: (limit?: number) =>
    get<{ items: AskHistoryItem[] }>("/knowledge-base/ask/history", {
      params: limit ? { limit } : undefined,
    }),

  // 同步管理
  triggerFullSync: () =>
    post<{ taskId: string; message: string }>("/knowledge-base/sync/full"),
  triggerDeltaSync: () =>
    post<{ taskId: string; message: string }>("/knowledge-base/sync/delta"),

  getSyncTaskStatus: (taskId: string) =>
    get<SyncTaskStatus>(`/knowledge-base/sync/tasks/${taskId}`),

  getSyncTasks: (params?: { limit?: number; offset?: number }) =>
    get<{ items: SyncTaskListItem[]; total: number }>(
      "/knowledge-base/sync/tasks",
      {
        params,
      },
    ),

  getSyncTaskSkippedItems: (
    taskId: string,
    params?: { limit?: number; offset?: number },
  ) =>
    get<{ items: SyncTaskSkippedItem[]; total: number }>(
      `/knowledge-base/sync/tasks/${taskId}/skipped`,
      { params },
    ),

  getSyncTaskProcessedItems: (
    taskId: string,
    params?: { limit?: number; offset?: number },
  ) =>
    get<{ items: SyncTaskProcessedItem[]; total: number }>(
      `/knowledge-base/sync/tasks/${taskId}/processed`,
      { params },
    ),
  getSyncTaskProcessingItems: (
    taskId: string,
    params?: { limit?: number; offset?: number },
  ) =>
    get<{ items: SyncTaskProcessedItem[]; total: number }>(
      `/knowledge-base/sync/tasks/${taskId}/processing`,
      { params },
    ),

  getSyncTaskFailedItems: (
    taskId: string,
    params?: { limit?: number; offset?: number },
  ) =>
    get<{ items: SyncTaskFailedItem[]; total: number }>(
      `/knowledge-base/sync/tasks/${taskId}/failed`,
      { params },
    ),
  terminateSyncTask: (taskId: string) =>
    post<SyncTaskStatus>(`/knowledge-base/sync/tasks/${taskId}/terminate`),

  // 度量统计
  getMetrics: () => get<MetricsOverview>("/knowledge-base/metrics"),

  getDailyTrend: (days?: number) =>
    get<{ items: DailyTrendItem[] }>("/knowledge-base/metrics/trend", {
      params: days ? { days } : undefined,
    }),

  getTopQueries: (days?: number, limit?: number) =>
    get<{ items: TopQueryItem[] }>("/knowledge-base/metrics/top-queries", {
      params: { days: days ?? 7, limit: limit ?? 10 },
    }),

  // 最近访问
  getRecentlyVisited: (limit?: number) =>
    get<{ items: RecentlyVisitedItem[] }>("/knowledge-base/recently-visited", {
      params: limit ? { limit } : undefined,
    }),

  recordView: (type: "document" | "article", id: string) =>
    apiClient.post(`/knowledge-base/view/${type}/${id}`),

  // 活动动态
  getActivityFeed: (filter?: "all" | "mentions" | "comments", limit?: number) =>
    get<{ items: ActivityItem[] }>("/knowledge-base/activity", {
      params: { filter: filter ?? "all", limit: limit ?? 20 },
    }),

  getUserAnalytics: () => get<UserAnalytics>("/knowledge-base/user-analytics"),

  getUnreadMentionCount: () =>
    get<{ count: number }>("/knowledge-base/unread-mentions"),

  markMentionAsRead: (id: string) =>
    apiClient.post(`/knowledge-base/mentions/${id}/read`),
};
